// What? A component for viewing and interacting with generated document content
// Why? To display AI-generated sections with editing, regeneration, and export capabilities
// How? Uses typewriter effect for streaming content, ReactMarkdown for rendering, and provides section-level controls

import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { MMEditor, createIconPackPlugin } from "@mmeditor/core";
import "@mmeditor/core/style.css";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  ModalClose,
  ModalDialog,
  Modal,
  Option,
  Select,
  Stack,
  Textarea,
  Tooltip,
  Typography,
} from "@mui/joy";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PsychologyIcon from "@mui/icons-material/Psychology";
//import Modal from '../common/Modal';

// Import MUI icons for MMEditor
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import CodeIcon from "@mui/icons-material/Code";
import DataObjectIcon from "@mui/icons-material/DataObject";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import LinkIcon from "@mui/icons-material/Link";
import TableChartIcon from "@mui/icons-material/TableChart";
import FormatClearIcon from "@mui/icons-material/FormatClear";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import DOMPurify from "dompurify";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import ReferenceItem from "./ReferenceItem";
import PdfViewer from "./PdfViewer";
import panelStyles from "./SourcePanel.module.css";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import Loading from "../../../../components/Loading";
import {
  StructureNavigator,
  buildTreeFromGenerationSections,
} from "../common/StructureNavigator";
import styles from "./GenerationView.module.css";
import {
  GenerationSection,
  GenerationState,
  ExportResult,
  ThinkingEvent,
} from "../../types/documentTypes";
import TurndownService from "turndown";
import type { Components } from "react-markdown";
import PromptPreviewModal from "./PromptPreviewModal";
import { useWorkspaceContext } from "../../../../providers/WorkspaceProvider";
import { useErrorMessageProvider } from "../../../../components/ErrorMessageProvider";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import SearchIcon from "@mui/icons-material/Search";
import CheckIcon from "@mui/icons-material/Check";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import useAuthToken from "../../../../utils/useAuthToken";
import { useFeatureFlag } from "../../../../providers/FeatureFlagProvider";
import QCSectionPanel from "../qc/QCSectionPanel";
import QCSectionErrorNotice from "../qc/QCSectionErrorNotice";
import useQualityCheck from "../../hooks/useQualityCheck";
import { QCSectionResult } from "../../types/qcTypes";
import { SectionCitation } from "../../types/citations";

// Create MUI icon pack configuration
const muiIconConfig = {
  icons: {
    bold: <FormatBoldIcon fontSize="small" />,
    italic: <FormatItalicIcon fontSize="small" />,
    underline: <FormatUnderlinedIcon fontSize="small" />,
    strike: <StrikethroughSIcon fontSize="small" />,
    code: <CodeIcon fontSize="small" />,
    "code-block": <DataObjectIcon fontSize="small" />,
    bullet: <FormatListBulletedIcon fontSize="small" />,
    number: <FormatListNumberedIcon fontSize="small" />,
    link: <LinkIcon fontSize="small" />,
    table: <TableChartIcon fontSize="small" />,
    clear: <FormatClearIcon fontSize="small" />,
  },
};

// Create icon pack plugin with MUI icons
const iconPackPlugin = createIconPackPlugin(muiIconConfig);

// Configure DOMPurify to preserve data-ref attributes on citation spans
const sanitizeHtml = (html: string): string =>
  DOMPurify.sanitize(html, { ADD_ATTR: ["data-ref"] });

const countQCFindings = (results: Map<number, QCSectionResult>): number =>
  Array.from(results.values()).reduce(
    (count, section) => count + section.findings.length,
    0,
  );

const getQCSectionsWithFindings = (
  results: Map<number, QCSectionResult>,
): QCSectionResult[] =>
  Array.from(results.values()).filter((section) => section.findings.length > 0);

// Strip data-ref spans from HTML, keeping inner text.
// Used before opening the editor so citation markers don't pollute contentEditable.
const stripDataRefSpans = (html: string): string =>
  html.replace(/<span data-ref="[^"]*">(.*?)<\/span>/gs, "$1");

// Custom rehype-sanitize schema that preserves data-ref on span elements
const rehypeSanitizeSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    span: [...(defaultSchema.attributes?.span || []), "dataRef"],
  },
};

// Configuration
interface Section {
  section_id: number;
  title: string;
  content: string;
  status: "complete" | "generating" | "validating";
  prompt?: string;
  order_index?: number;
  modified_date?: string | null;
  level?: number;
}

interface GenerationViewProps {
  documentId: number;
  templateId?: number;
  documentStatus?: string;
  isGenerating: boolean;
  setIsGenerating: (generating: boolean) => void;
  generationState: GenerationState;
  startGeneration: (continueGeneration: boolean) => void;
  exportDocument: (
    documentId: number,
    workspace_id: string,
    templateId?: number,
  ) => Promise<ExportResult>;
  downloadExport: (
    documentIdOrPath: string,
    workspace_id: string,
  ) => Promise<Blob>;
  downloadFileToBrowser: (
    exportPath: string,
    workspace_id: string,
  ) => Promise<void>;
  regenerateSection: (
    documentId: number,
    sectionId: number,
    workspace_id: string,
    customPrompt?: string,
  ) => Promise<any>;
  onRegenerationActivityChange?: (isActive: boolean) => void;
  /**
   * Non-empty while a document mutation outside this view (source upload,
   * source deletion, config save) makes regeneration unsafe. Doubles as the
   * tooltip explaining why the action is disabled.
   */
  regenerateBlockedReason?: string;
  getDocumentSections: (
    documentId: number,
    workspace_id: string,
  ) => Promise<GenerationSection[]>;
  updateGenerationState: (
    updates:
      | Partial<GenerationState>
      | ((prev: GenerationState) => Partial<GenerationState>),
  ) => void;
  updateSection: (
    documentId: number,
    sectionId: number,
    content: string,
    workspace_id: string,
  ) => Promise<any>;
  getSectionPrompt: (
    documentId: number,
    sectionId: number,
    workspace_id: string,
  ) => Promise<any>;
  hasSourceDocuments?: boolean;
  // Source verification props
  sourceVerificationEnabled?: boolean;
  isPanelOpen?: boolean;
  onTogglePanel?: () => void;
  citations?: Record<number, SectionCitation[]>;
  validationStatus?: Record<number, "validating" | "complete">;
  validationErrors?: Record<number, string>;
  expandedRef?: { sectionId: number; refId: string } | null;
  onExpandRef?: (sectionId: number, refId: string) => void;
  onUpdateSectionHeight?: (sectionId: number, height: number) => void;
  onClearSectionCitations?: (sectionId: number) => void;
  onValidatingSection?: (sectionId: number) => void;
  citationsLoaded?: boolean;
}

const GenerationView: React.FC<GenerationViewProps> = ({
  documentId,
  templateId,
  documentStatus,
  isGenerating,
  setIsGenerating,
  generationState,
  startGeneration,
  exportDocument,
  downloadExport,
  downloadFileToBrowser,
  regenerateSection,
  onRegenerationActivityChange,
  regenerateBlockedReason = "",
  getDocumentSections,
  updateGenerationState,
  updateSection,
  getSectionPrompt,
  hasSourceDocuments = false,
  sourceVerificationEnabled = false,
  isPanelOpen = false,
  onTogglePanel,
  citations = {},
  validationStatus = {},
  validationErrors = {},
  expandedRef,
  onExpandRef,
  onUpdateSectionHeight,
  onClearSectionCitations,
  onValidatingSection,
  citationsLoaded = false,
}) => {
  // For typewriter effect
  const [displayedText, setDisplayedText] = useState("");

  // Add these state variables at the top of the component
  const [selectedSection, setSelectedSection] = useState<Section | null>(null);
  const [isRegenerateModalOpen, setIsRegenerateModalOpen] = useState(false);
  const [regenerationError, setRegenerationError] = useState<string | null>(
    null,
  );
  // Specific section regeneration overlay (lifted above callbacks)
  const [regeneratingSectionId, setRegeneratingSectionId] = useState<
    number | null
  >(null);

  useEffect(() => {
    onRegenerationActivityChange?.(regeneratingSectionId !== null);
  }, [onRegenerationActivityChange, regeneratingSectionId]);

  useEffect(
    () => () => {
      onRegenerationActivityChange?.(false);
    },
    [onRegenerationActivityChange],
  );

  // Thinking sections expanded state
  const [expandedThinkingSections, setExpandedThinkingSections] = useState<
    Set<number>
  >(new Set());

  // PDF viewer state
  const [pdfView, setPdfView] = useState<{
    sourceDocumentId: number;
    page: number;
    sourceFilename: string;
  } | null>(null);

  // QC feature flag + state
  const qcEnabled = useFeatureFlag("meridian_qc");
  const [qcStatus, setQcStatus] = useState<
    "idle" | "running" | "completed" | "failed"
  >("idle");
  const [qcSectionResults, setQcSectionResults] = useState<
    Map<number, QCSectionResult>
  >(new Map());
  const [qcActiveSectionIndex, setQcActiveSectionIndex] = useState<
    number | null
  >(null);
  const [qcTotalFindings, setQcTotalFindings] = useState(0);
  const [qcErrorMessage, setQcErrorMessage] = useState<string | null>(null);
  const [qcModalSectionIndex, setQcModalSectionIndex] = useState<number | null>(
    null,
  );
  const [qcApplyingSectionIndex, setQcApplyingSectionIndex] = useState<
    number | null
  >(null);

  const {
    startDocumentQC,
    connectToStream,
    acceptFinding,
    declineFinding,
    revertFinding,
    bulkResolve,
    applyChanges,
    submitFeedback,
    cancel,
  } = useQualityCheck();

  // Auth token for QC SSE stream (EventSource cannot set headers)
  const getAuthToken = useAuthToken();

  const resetQCDisplayState = useCallback(() => {
    setQcSectionResults(new Map());
    setQcTotalFindings(0);
    setQcErrorMessage(null);
    setQcModalSectionIndex(null);
  }, []);

  // Toggle thinking section expansion
  const toggleThinkingExpanded = useCallback((sectionId: number) => {
    setExpandedThinkingSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  }, []);

  // Section height tracking for source verification panel alignment
  useEffect(() => {
    if (!onUpdateSectionHeight || !isPanelOpen) return;
    const sectionEls = document.querySelectorAll("[data-section-id]");
    const observers: ResizeObserver[] = [];
    sectionEls.forEach((el) => {
      const sectionId = Number(el.getAttribute("data-section-id"));
      if (isNaN(sectionId)) return;
      const observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          onUpdateSectionHeight(sectionId, entry.contentRect.height);
        }
      });
      observer.observe(el);
      observers.push(observer);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, [
    isPanelOpen,
    onUpdateSectionHeight,
    generationState.completedSections.length,
    generationState.currentSection?.section_id,
  ]);

  // Handle clicks on data-ref spans in the editor content
  useEffect(() => {
    if (!isPanelOpen || !onExpandRef) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const refSpan = target.closest("[data-ref]") as HTMLElement | null;
      if (!refSpan) return;
      const refId = refSpan.getAttribute("data-ref");
      if (!refId) return;
      // Walk up to find the section container with data-section-id
      const sectionEl = refSpan.closest(
        "[data-section-id]",
      ) as HTMLElement | null;
      if (!sectionEl) return;
      const sectionId = Number(sectionEl.getAttribute("data-section-id"));
      if (!isNaN(sectionId)) {
        onExpandRef(sectionId, refId);
      }
    };
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [isPanelOpen, onExpandRef]);

  // Apply citation highlight classes when panel is open
  // Uses MutationObserver to re-apply when React re-renders section DOM
  useEffect(() => {
    if (!isPanelOpen) {
      document
        .querySelectorAll(
          ".citation-highlight-verified, .citation-highlight-unverified, .citation-highlight-acceptable",
        )
        .forEach((el) => {
          el.classList.remove(
            "citation-highlight-verified",
            "citation-highlight-unverified",
            "citation-highlight-acceptable",
          );
        });
      return;
    }
    const applyHighlights = () => {
      Object.entries(citations).forEach(([sectionIdStr, sectionCitations]) => {
        const sectionId = Number(sectionIdStr);
        const sectionEl = document.querySelector(
          `[data-section-id="${sectionId}"]`,
        );
        if (!sectionEl) return;
        sectionCitations.forEach((citation) => {
          const spans = sectionEl.querySelectorAll(
            `[data-ref="${citation.ref_id}"]`,
          );
          spans.forEach((span) => {
            if (
              !span.classList.contains("citation-highlight-verified") &&
              !span.classList.contains("citation-highlight-unverified") &&
              !span.classList.contains("citation-highlight-acceptable")
            ) {
              if (citation.verdict === "acceptable_variation") {
                span.classList.add("citation-highlight-acceptable");
              } else if (
                citation.source_type !== "Source" ||
                citation.verified
              ) {
                span.classList.add("citation-highlight-verified");
              } else {
                span.classList.add("citation-highlight-unverified");
                // If there's a divergent phrase, wrap it with a special class
                if (citation.divergent_phrase && span.textContent) {
                  const text = span.textContent;
                  const divergent = citation.divergent_phrase;
                  const idx = text.indexOf(divergent);
                  if (idx >= 0) {
                    const before = document.createTextNode(text.slice(0, idx));
                    const wrapper = document.createElement("span");
                    wrapper.className = "citation-divergent-phrase";
                    wrapper.textContent = text.slice(
                      idx,
                      idx + divergent.length,
                    );
                    const after = document.createTextNode(
                      text.slice(idx + divergent.length),
                    );
                    span.textContent = "";
                    span.appendChild(before);
                    span.appendChild(wrapper);
                    span.appendChild(after);
                  }
                }
              }
            }
          });
        });
      });
    };
    // Apply immediately and again after next paint
    applyHighlights();
    const rafId = requestAnimationFrame(applyHighlights);

    // Watch for DOM mutations (React re-renders via dangerouslySetInnerHTML)
    // to re-apply highlights when section content is replaced
    const sectionEls = document.querySelectorAll("[data-section-id]");
    const observers: MutationObserver[] = [];
    sectionEls.forEach((el) => {
      const obs = new MutationObserver(() => {
        requestAnimationFrame(applyHighlights);
      });
      obs.observe(el, { childList: true, subtree: true });
      observers.push(obs);
    });

    return () => {
      cancelAnimationFrame(rafId);
      observers.forEach((obs) => obs.disconnect());
    };
  }, [
    isPanelOpen,
    citations,
    generationState.completedSections.length,
    expandedRef,
  ]);

  const RichTextEditor = ({
    content,
    onSave,
    onCancel,
  }: {
    content: string;
    onSave: (content: string) => void;
    onCancel: () => void;
  }) => {
    const [currentContent, setCurrentContent] = useState(content);
    const [isLoading, setIsLoading] = useState(false);
    const editorRef = useRef<HTMLDivElement>(null);

    // Update content when prop changes
    useEffect(() => {
      setCurrentContent(content);
    }, [content]);

    // Apply overflow fixes after editor renders
    useEffect(() => {
      const applyOverflowFixes = () => {
        if (editorRef.current) {
          // Inject minimal CSS to fix text overflow only
          const styleId = "mmeditor-overflow-fix";
          let existingStyle = document.getElementById(styleId);
          if (!existingStyle) {
            existingStyle = document.createElement("style");
            existingStyle.id = styleId;
            existingStyle.textContent = `
              /* MMEditor contenteditable overflow fixes */
              [contenteditable] {
                word-wrap: break-word !important;
                overflow-wrap: break-word !important;
                overflow-x: hidden !important;
              }

              /* MMEditor table picker positioning */
              [class*="picker"], [class*="_picker_"] {
                position: absolute !important;
                top: 50px !important;
                left: auto !important;
                right: 10px !important;
                bottom: auto !important;
                max-width: 300px !important;
                max-height: 200px !important;
                overflow: hidden !important;
                z-index: 1000 !important;
              }
            `;
            document.head.appendChild(existingStyle);
          }

          // Force the editorWrapper container to respect width constraints
          if (editorRef.current) {
            editorRef.current.style.width = "100%";
            editorRef.current.style.maxWidth = "100%";
            editorRef.current.style.minWidth = "0";
            editorRef.current.style.boxSizing = "border-box";
            editorRef.current.style.overflowX = "hidden";
          }

          // Apply overflow constraints to contenteditable elements
          const contentEditableElements =
            editorRef.current.querySelectorAll("[contenteditable]");
          contentEditableElements.forEach((element: Element) => {
            const htmlElement = element as HTMLElement;
            htmlElement.style.boxSizing = "border-box";
            htmlElement.style.maxWidth = "100%";
            htmlElement.style.wordWrap = "break-word";
            htmlElement.style.overflowWrap = "break-word";
            htmlElement.style.overflowX = "hidden";
          });

          // Fix MMEditor table picker positioning to prevent overflow
          const tablePickers = editorRef.current.querySelectorAll(
            '[class*="picker"], [class*="_picker_"]',
          );
          tablePickers.forEach((picker: Element) => {
            const htmlPicker = picker as HTMLElement;
            if (
              htmlPicker.style.position === "absolute" ||
              htmlPicker.style.position === "fixed"
            ) {
              htmlPicker.style.position = "absolute";
              htmlPicker.style.top = "50px";
              htmlPicker.style.left = "auto";
              htmlPicker.style.right = "10px";
              htmlPicker.style.bottom = "auto";
              htmlPicker.style.maxWidth = "300px";
              htmlPicker.style.maxHeight = "200px";
              htmlPicker.style.overflow = "hidden";
              htmlPicker.style.zIndex = "1000";
            }
          });
        }
      };

      // Apply fixes immediately and on any changes
      applyOverflowFixes();

      // Set up a mutation observer to apply fixes when DOM changes
      const observer = new MutationObserver(applyOverflowFixes);
      if (editorRef.current) {
        observer.observe(editorRef.current, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ["style", "class"],
        });
      }

      // Periodically re-apply fixes to handle dynamic MMEditor changes
      const interval = setInterval(applyOverflowFixes, 500);

      return () => {
        observer.disconnect();
        clearInterval(interval);
      };
    }, [currentContent]);

    const handleSave = async () => {
      setIsLoading(true);
      try {
        await onSave(currentContent);
      } catch (error) {
        console.error("Error saving content:", error);
      } finally {
        setIsLoading(false);
      }
    };

    const handleCancel = () => {
      setCurrentContent(content); // Reset to original content
      onCancel(); // Exit edit mode
    };

    // MMEditor configuration is handled internally

    return (
      <div
        ref={editorRef}
        className={styles.editorWrapper}
        style={{
          width: "100%",
          maxWidth: "100%",
          minWidth: "0",
          overflowY: "auto",
          overflowX: "hidden",
          position: "relative",
          boxSizing: "border-box",
        }}
      >
        <MMEditor
          value={currentContent}
          onChange={setCurrentContent}
          plugins={[iconPackPlugin]}
          style={{
            height: "400px",
            width: "100%",
            maxWidth: "100%",
            overflowY: "auto",
            overflowX: "hidden",
          }}
          placeholder="Edit your content here..."
        />
        <Stack
          direction="row"
          spacing={1}
          justifyContent="flex-end"
          sx={{ p: 1, borderTop: "1px solid", borderColor: "neutral.300" }}
        >
          <Button
            variant="outlined"
            color="neutral"
            onClick={handleCancel}
            disabled={isLoading}
            startDecorator={<CloseIcon />}
            sx={{ backgroundColor: "background.surface" }}
          >
            Cancel
          </Button>
          <Button
            variant="solid"
            color="primary"
            onClick={handleSave}
            disabled={isLoading}
            startDecorator={
              isLoading ? <CircularProgress size="sm" /> : <SaveIcon />
            }
          >
            {isLoading ? "Saving..." : "Save Changes"}
          </Button>
        </Stack>
      </div>
    );
  };

  // Add workspace_id from context
  const { workspace } = useWorkspaceContext();
  const workspace_id = workspace?.id || "";

  // Global error handling
  const { registerError } = useErrorMessageProvider();

  // Helper function to detect if content is HTML or markdown
  const isHtmlContent = (content: string): boolean => {
    if (!content) return false;

    // Check for common HTML tags
    const htmlTags = /<[^>]+>/;
    const hasHtmlTags = htmlTags.test(content);

    // Check for markdown patterns
    const markdownPatterns = [
      /^#{1,6}\s/, // Headers
      /^\*\s/, // Bullet points
      /^\d+\.\s/, // Numbered lists
      /\[.*\]\(.*\)/, // Links
      /\*\*.*\*\*/, // Bold
      /\*.*\*/, // Italic
      /`.*`/, // Code
      /^\s*\|.*\|.*\|/, // Tables
    ];

    const hasMarkdownPatterns = markdownPatterns.some((pattern) =>
      pattern.test(content),
    );

    // If it has HTML tags and no markdown patterns, it's HTML
    // If it has markdown patterns and no HTML tags, it's markdown
    // If it has both, prefer HTML (since we're transitioning to HTML)
    return hasHtmlTags && !hasMarkdownPatterns;
  };

  // Section loading on mount is handled by GenerateDocument.tsx (parent component).
  // Removed duplicate useEffect here that was firing between sections (when currentSection
  // goes null) and prematurely setting isComplete: true from partial DB results.

  // Section handling is managed by useDocuments hook - removed duplicate handlers to prevent conflicts

  // Handle typewriter effect
  const lastUpdateTime = useRef<number>(0);
  const accumulatedTime = useRef<number>(0);
  const lastCharTime = useRef<number>(0);

  useEffect(() => {
    if (!generationState.currentSection) return;

    // Always reset state when starting a new section
    setDisplayedText("");
    lastUpdateTime.current = performance.now();
    lastCharTime.current = performance.now();
    accumulatedTime.current = 0;

    const updateText = () => {
      const currentSection = generationState.currentSection;
      if (!currentSection) return;

      // Thinking sections are not typed out — their transition is driven by the
      // backend "complete" event, not the typewriter. Firing isStreamComplete here
      // would prematurely promote the Thinking section before its events arrive.
      if (currentSection.section_type === "Thinking") return;

      setDisplayedText(currentSection.content);

      if (
        generationState.isComplete &&
        generationState.pendingSections.length === 0
      ) {
        setIsGenerating(false);
        updateGenerationState({
          isStreamComplete: true,
          isWaitingForNext: false,
        });
      } else {
        updateGenerationState({
          isStreamComplete: true,
          isWaitingForNext: generationState.pendingSections.length > 0,
        });
      }
      return;
    };

    updateText();
    //requestAnimationFrame(updateText);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally only triggers on section_id change
  }, [generationState.currentSection?.section_id]);

  // Separate effect for handling section transitions
  useEffect(() => {
    if (
      generationState.isStreamComplete &&
      generationState.pendingSections.length > 0
    ) {
      updateGenerationState((prev) => {
        // Never promote away from a Thinking section — it manages its own transitions
        if (prev.currentSection?.section_type === "Thinking") {
          return { isStreamComplete: false, isWaitingForNext: false };
        }
        const nextSection = prev.pendingSections[0];
        if (!nextSection) {
          console.warn("Invalid next section detected:", nextSection);
          return { pendingSections: prev.pendingSections.slice(1) };
        }
        if (nextSection.section_id === prev.currentSection?.section_id) {
          return {
            pendingSections: prev.pendingSections.slice(1),
            isWaitingForNext: false,
            isStreamComplete: true,
          };
        }
        const currentSection = prev.currentSection;
        return {
          completedSections: currentSection
            ? [
                ...prev.completedSections,
                {
                  ...currentSection,
                  status:
                    currentSection.status === "validating"
                      ? ("validating" as const)
                      : ("complete" as const),
                },
              ]
            : prev.completedSections,
          currentSection: {
            ...nextSection,
            status: "generating" as const,
          },
          pendingSections: prev.pendingSections.slice(1),
          isWaitingForNext: false,
          isStreamComplete: true,
        };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- triggers only on stream completion state changes
  }, [
    generationState.isStreamComplete,
    generationState.pendingSections.length,
  ]);

  // Finalize the last section after typing finishes when generation is complete
  useEffect(() => {
    const section = generationState.currentSection;
    if (!section) return;

    const finishedTyping =
      displayedText.length >= (section.content?.length || 0);
    const noMorePending = generationState.pendingSections.length === 0;

    if (generationState.isComplete && noMorePending && finishedTyping) {
      updateGenerationState((prev) => {
        const s = prev.currentSection;
        if (!s) return {};
        const alreadyCompleted = prev.completedSections.some(
          (c) => c.section_id === s.section_id,
        );
        if (!alreadyCompleted) {
          return {
            completedSections: [
              ...prev.completedSections,
              {
                ...s,
                status:
                  s.status === "validating"
                    ? ("validating" as const)
                    : ("complete" as const),
              },
            ],
            currentSection: null,
            isStreamComplete: true,
            isWaitingForNext: false,
          };
        } else if (prev.currentSection) {
          return { currentSection: null };
        }
        return {};
      });
    }
  }, [
    generationState.isComplete,
    generationState.pendingSections.length,
    generationState.currentSection?.section_id,
    displayedText,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]);

  const [downloadingExport, setDownloadingExport] = useState(false);

  const handleExport = async () => {
    try {
      const result = await exportDocument(documentId, workspace_id, templateId);
      if (!result.success) {
        console.error("Export failed:", result.error);
        registerError(result.error || "Export failed");
      }
    } catch (error) {
      console.error("Export error:", error);
      registerError("Failed to export document");
    }
  };

  const handleDownload = async () => {
    if (
      generationState.downloadUrl &&
      generationState.exportState === "ready"
    ) {
      try {
        setDownloadingExport(true);
        const exportPath = generationState.downloadUrl;
        downloadFileToBrowser(exportPath, workspace_id);

        updateGenerationState({
          exportState: "idle",
          downloadUrl: undefined,
        });
      } catch (error) {
        console.error("Download failed:", error);
        updateGenerationState({ exportState: "failed" });
      } finally {
        setDownloadingExport(false);
      }
    }
  };

  const getExportButtonText = () => {
    switch (generationState.exportState) {
      case "exporting":
        return "Exporting...";
      case "ready":
        return "Download";
      case "failed":
        return "Export failed";
      default:
        return "Export";
    }
  };

  // --- QC Handlers ---

  const refreshSections = useCallback(async () => {
    try {
      const sections = await getDocumentSections(documentId, workspace_id);
      if (sections && sections.length > 0) {
        const completedSections = sections.map((section) => ({
          section_id: section.section_id,
          title: section.title,
          content: section.content,
          prompt: section.prompt || "",
          level: section.level,
          status: "complete" as const,
          order_index: section.order_index,
          modified_date: section.modified_date,
          section_type: section.section_type,
          thinking_events: section.thinking_events,
        }));
        updateGenerationState({
          completedSections,
          isComplete: true,
          isStreamComplete: true,
        });
      }
    } catch (error) {
      console.error("Error refreshing sections:", error);
    }
  }, [documentId, workspace_id, getDocumentSections, updateGenerationState]);

  const wireQCStream = useCallback(
    async (runId: number) => {
      const token = await getAuthToken();
      connectToStream(runId, token, workspace_id, {
        onStarted: () => {
          // Mode A doesn't render QCProgressView, so total is not needed.
        },
        onSectionStart: (index, title, content, sectionId) => {
          setQcSectionResults((prev) => {
            const next = new Map(prev);
            next.set(index, {
              sectionIndex: index,
              sectionId,
              sectionTitle: title,
              sectionContent: content,
              status: "checking",
              findings: [],
              findingCount: 0,
            });
            return next;
          });
        },
        onFinding: (finding) => {
          setQcSectionResults((prev) => {
            const next = new Map(prev);
            const section = next.get(finding.SectionIndex);
            if (section) {
              next.set(finding.SectionIndex, {
                ...section,
                findings: [...section.findings, finding],
                findingCount: section.findingCount + 1,
              });
            }
            return next;
          });
          setQcTotalFindings((prev) => prev + 1);
        },
        onSectionComplete: (index, count) => {
          setQcSectionResults((prev) => {
            const next = new Map(prev);
            const section = next.get(index);
            if (section) {
              next.set(index, {
                ...section,
                status: "complete",
                findingCount: count,
              });
            }
            return next;
          });
        },
        onSectionError: (index, count, error, section) => {
          setQcErrorMessage(error);
          setQcSectionResults((prev) => {
            const next = new Map(prev);
            const existing = next.get(index);
            next.set(index, {
              sectionIndex: index,
              sectionId: section?.sectionId ?? existing?.sectionId,
              sectionTitle:
                section?.sectionTitle ||
                existing?.sectionTitle ||
                "Unknown section",
              sectionContent: existing?.sectionContent || "",
              findings: existing?.findings || [],
              findingCount: count,
              status: "error",
              errorMessage: error,
            });
            return next;
          });
        },
        onComplete: (_total) => {
          setQcStatus("completed");
          // Always open the modal when QC finishes
          setQcSectionResults((currentResults) => {
            // `_total` from the stream is per-run only, so a single-section
            // re-check would clobber findings already accumulated from earlier
            // section QCs. Recompute from the full results map instead.
            setQcTotalFindings(countQCFindings(currentResults));
            // Prefer findings, then section-level QC errors, then the first section.
            let targetIndex: number | null = null;
            for (const [idx, sec] of currentResults) {
              if (sec.findings.length > 0) {
                targetIndex = idx;
                break;
              }
              if (sec.status === "error" && targetIndex === null) {
                targetIndex = idx;
              }
            }
            if (targetIndex === null) {
              for (const idx of currentResults.keys()) {
                targetIndex = idx;
                break;
              }
            }
            if (targetIndex !== null) {
              setQcModalSectionIndex(targetIndex);
            }
            return currentResults; // no change to the map itself
          });
        },
        onError: (error, section) => {
          setQcStatus("failed");
          setQcErrorMessage(error);
          if (section?.sectionIndex !== undefined) {
            const failedSectionIndex = section.sectionIndex;
            setQcModalSectionIndex(failedSectionIndex);
            setQcSectionResults((prev) => {
              const next = new Map(prev);
              const existing = next.get(failedSectionIndex);
              next.set(failedSectionIndex, {
                sectionIndex: failedSectionIndex,
                sectionId: section.sectionId,
                sectionTitle:
                  section.sectionTitle ||
                  existing?.sectionTitle ||
                  "Unknown section",
                sectionContent: existing?.sectionContent || "",
                findings: existing?.findings || [],
                findingCount: existing?.findingCount || 0,
                status: "error",
                errorMessage: error,
              });
              return next;
            });
          }
          console.error("QC stream error:", error);
        },
      });
    },
    [connectToStream, getAuthToken, workspace_id],
  );

  const handleStartSectionQC = useCallback(
    async (sectionId: number, sectionIndex: number) => {
      if (!hasSourceDocuments) return;
      try {
        const { qc_run_id } = await startDocumentQC(
          documentId,
          workspace_id,
          [sectionId],
          "integrated",
        );
        setQcSectionResults((prev) => {
          const next = new Map(prev);
          next.delete(sectionIndex);
          setQcTotalFindings(countQCFindings(next));
          return next;
        });
        setQcModalSectionIndex((cur) => (cur === sectionIndex ? null : cur));
        setQcStatus("running");
        setQcActiveSectionIndex(sectionIndex);
        wireQCStream(qc_run_id);
      } catch (err) {
        console.error("Failed to start section QC:", err);
      }
    },
    [
      documentId,
      workspace_id,
      hasSourceDocuments,
      startDocumentQC,
      wireQCStream,
    ],
  );

  const handleStartAllSectionsQC = useCallback(async () => {
    if (!hasSourceDocuments) return;
    // Flip UI to "Checking..." immediately so the user gets instant feedback
    // instead of waiting for the startDocumentQC round-trip to resolve.
    resetQCDisplayState();
    setQcStatus("running");
    setQcActiveSectionIndex(null);
    try {
      const { qc_run_id } = await startDocumentQC(
        documentId,
        workspace_id,
        null,
        "integrated",
      );
      wireQCStream(qc_run_id);
    } catch (err) {
      console.error("Failed to start QC:", err);
      setQcStatus("failed");
      setQcErrorMessage(
        err instanceof Error ? err.message : "Failed to start quality check",
      );
    }
  }, [
    documentId,
    workspace_id,
    hasSourceDocuments,
    startDocumentQC,
    resetQCDisplayState,
    wireQCStream,
  ]);

  // Findings from older per-section QC runs may coexist with the current run,
  // so each resolution call must target the finding's own QCRunID.
  const findRunIdForFinding = useCallback(
    (findingId: number): number | null => {
      for (const section of qcSectionResults.values()) {
        const f = section.findings.find((x) => x.FindingID === findingId);
        if (f) return f.QCRunID;
      }
      return null;
    },
    [qcSectionResults],
  );

  const findRunIdForSection = useCallback(
    (sectionIndex: number): number | null => {
      const section = qcSectionResults.get(sectionIndex);
      return section?.findings[0]?.QCRunID ?? null;
    },
    [qcSectionResults],
  );

  const handleAcceptFinding = useCallback(
    async (findingId: number, editedText?: string | null) => {
      const runId = findRunIdForFinding(findingId);
      if (!runId) return;
      await acceptFinding(runId, findingId, workspace_id, editedText);
      setQcSectionResults((prev) => {
        const next = new Map(prev);
        for (const [key, section] of next) {
          const finding = section.findings.find(
            (f) => f.FindingID === findingId,
          );
          if (finding) {
            finding.Resolution = "accepted";
            finding.Helpful = null;
            if (editedText != null) {
              finding.EditedText = editedText;
            }
            next.set(key, { ...section, findings: [...section.findings] });
            break;
          }
        }
        return next;
      });
    },
    [findRunIdForFinding, workspace_id, acceptFinding],
  );

  const handleDeclineFinding = useCallback(
    async (findingId: number) => {
      const runId = findRunIdForFinding(findingId);
      if (!runId) return;
      await declineFinding(runId, findingId, workspace_id);
      setQcSectionResults((prev) => {
        const next = new Map(prev);
        for (const [key, section] of next) {
          const finding = section.findings.find(
            (f) => f.FindingID === findingId,
          );
          if (finding) {
            finding.Resolution = "declined";
            finding.Helpful = null;
            next.set(key, { ...section, findings: [...section.findings] });
            break;
          }
        }
        return next;
      });
    },
    [findRunIdForFinding, workspace_id, declineFinding],
  );

  const handleRevertFinding = useCallback(
    async (findingId: number) => {
      const runId = findRunIdForFinding(findingId);
      if (!runId) return;
      await revertFinding(runId, findingId, workspace_id);
      setQcSectionResults((prev) => {
        const next = new Map(prev);
        for (const [key, section] of next) {
          const finding = section.findings.find(
            (f) => f.FindingID === findingId,
          );
          if (finding) {
            finding.Resolution = null;
            finding.ResolvedDate = null;
            finding.Helpful = null;
            finding.EditedText = null;
            next.set(key, { ...section, findings: [...section.findings] });
            break;
          }
        }
        return next;
      });
    },
    [findRunIdForFinding, workspace_id, revertFinding],
  );

  const handleAcceptAllInSection = useCallback(
    async (sectionIndex: number) => {
      const runId = findRunIdForSection(sectionIndex);
      if (!runId) return;
      await bulkResolve(runId, "accepted", workspace_id, sectionIndex);
      setQcSectionResults((prev) => {
        const next = new Map(prev);
        const section = next.get(sectionIndex);
        if (section) {
          section.findings = section.findings.map((f) => ({
            ...f,
            Resolution: "accepted" as const,
            Helpful: null,
          }));
          next.set(sectionIndex, { ...section });
        }
        return next;
      });
    },
    [findRunIdForSection, workspace_id, bulkResolve],
  );

  const handleDeclineAllInSection = useCallback(
    async (sectionIndex: number) => {
      const runId = findRunIdForSection(sectionIndex);
      if (!runId) return;
      await bulkResolve(runId, "declined", workspace_id, sectionIndex);
      setQcSectionResults((prev) => {
        const next = new Map(prev);
        const section = next.get(sectionIndex);
        if (section) {
          section.findings = section.findings.map((f) => ({
            ...f,
            Resolution: "declined" as const,
            Helpful: null,
          }));
          next.set(sectionIndex, { ...section });
        }
        return next;
      });
    },
    [findRunIdForSection, workspace_id, bulkResolve],
  );

  const handleRevertAllInSection = useCallback(
    async (sectionIndex: number) => {
      const runId = findRunIdForSection(sectionIndex);
      if (!runId) return;
      await bulkResolve(runId, null, workspace_id, sectionIndex);
      setQcSectionResults((prev) => {
        const next = new Map(prev);
        const s = next.get(sectionIndex);
        if (s) {
          s.findings = s.findings.map((f) => ({
            ...f,
            Resolution: null,
            ResolvedDate: null,
            Helpful: null,
            EditedText: null,
          }));
          next.set(sectionIndex, { ...s });
        }
        return next;
      });
    },
    [findRunIdForSection, workspace_id, bulkResolve],
  );

  const handleDismissQC = useCallback(() => {
    cancel();
    setQcStatus("idle");
    setQcSectionResults(new Map());
    setQcActiveSectionIndex(null);
    setQcTotalFindings(0);
    setQcModalSectionIndex(null);
  }, [cancel]);

  useEffect(() => {
    if (!hasSourceDocuments && qcStatus !== "idle") {
      handleDismissQC();
    }
  }, [hasSourceDocuments, handleDismissQC, qcStatus]);

  const handleApplySectionChanges = useCallback(
    async (sectionIndex: number) => {
      const runId = findRunIdForSection(sectionIndex);
      if (!runId) return;
      setQcApplyingSectionIndex(sectionIndex);
      try {
        await applyChanges(runId, workspace_id, sectionIndex);
        await refreshSections();

        const nextResults = new Map(qcSectionResults);
        nextResults.delete(sectionIndex);
        const remainingCount = countQCFindings(nextResults);
        const remainingSections = getQCSectionsWithFindings(nextResults);

        if (remainingCount === 0) {
          handleDismissQC();
          return;
        }

        setQcSectionResults(nextResults);
        setQcTotalFindings(remainingCount);
        setQcModalSectionIndex(remainingSections[0].sectionIndex);
      } finally {
        setQcApplyingSectionIndex(null);
      }
    },
    [
      findRunIdForSection,
      workspace_id,
      applyChanges,
      refreshSections,
      qcSectionResults,
      handleDismissQC,
    ],
  );

  const handleSubmitFeedback = useCallback(
    async (findingId: number, helpful: boolean) => {
      const runId = findRunIdForFinding(findingId);
      if (!runId) return;
      await submitFeedback(runId, findingId, helpful, workspace_id);
      setQcSectionResults((prev) => {
        const next = new Map(prev);
        for (const [key, section] of next) {
          const finding = section.findings.find(
            (f) => f.FindingID === findingId,
          );
          if (finding) {
            finding.Helpful = helpful;
            next.set(key, { ...section, findings: [...section.findings] });
            break;
          }
        }
        return next;
      });
    },
    [findRunIdForFinding, workspace_id, submitFeedback],
  );

  const wasGeneratingRef = useRef(false);
  useEffect(() => {
    const startedGenerating =
      generationState.isGenerating && !wasGeneratingRef.current;
    wasGeneratingRef.current = generationState.isGenerating;

    if (!startedGenerating) return;

    const isFreshGeneration =
      generationState.completedSections.length === 0 &&
      !generationState.currentSection &&
      generationState.pendingSections.length === 0;

    if (isFreshGeneration) {
      handleDismissQC();
    }
  }, [
    generationState.completedSections.length,
    generationState.currentSection,
    generationState.isGenerating,
    generationState.pendingSections.length,
    handleDismissQC,
  ]);

  // Add these handlers
  const handleRegenerateClick = useCallback((section: Section) => {
    setSelectedSection(section);
    setIsRegenerateModalOpen(true);
  }, []);

  const handleRegenerateSection = useCallback(
    async (formData: Record<string, string>) => {
      if (!selectedSection) return;
      // The modal can outlive the state it was opened in — a source upload or
      // save started after opening it must still block the request.
      if (regenerateBlockedReason) {
        setRegenerationError(regenerateBlockedReason);
        return;
      }

      setRegenerationError(null);
      setIsRegenerateModalOpen(false);
      // Track which section is being regenerated to show a local spinner
      const regeneratingId = selectedSection.section_id;
      setRegeneratingSectionId(regeneratingId);

      const isCurrentSection =
        generationState.currentSection?.section_id ===
        selectedSection.section_id;
      const completedIndex = generationState.completedSections.findIndex(
        (s) => s.section_id === selectedSection.section_id,
      );

      // Create a temporary loading section
      const loadingSection: GenerationSection = {
        section_id: selectedSection.section_id,
        title: selectedSection.title,
        content: "", // Empty content while loading
        prompt: selectedSection.prompt,
        level: selectedSection.level,
        order_index: selectedSection.order_index,
        status: "generating" as const,
      };

      // Create a properly typed version of the original section
      const originalSection: GenerationSection = {
        section_id: selectedSection.section_id,
        title: selectedSection.title,
        content: selectedSection.content,
        prompt: selectedSection.prompt,
        level: selectedSection.level,
        status: "complete" as const,
      };

      // Update state to show loading
      if (isCurrentSection) {
        updateGenerationState({
          currentSection: loadingSection,
        });
      } else if (completedIndex !== -1) {
        const newCompletedSections = [...generationState.completedSections];
        newCompletedSections[completedIndex] = loadingSection;
        updateGenerationState({
          completedSections: newCompletedSections,
        });
      }

      try {
        const result = await regenerateSection(
          documentId,
          selectedSection.section_id,
          workspace_id,
          formData.prompt,
        );

        if (result.success && result.data) {
          const updatedSection: GenerationSection = {
            section_id: result.data.section_id,
            title: result.data.title,
            content: result.data.content,
            prompt: result.data.prompt,
            level: result.data.level,
            order_index: result.data.order_index ?? selectedSection.order_index,
            status: "complete" as const,
          };

          if (isCurrentSection) {
            updateGenerationState({ currentSection: updatedSection });
          } else if (completedIndex !== -1) {
            updateGenerationState((prev) => {
              const currentIndex = prev.completedSections.findIndex(
                (s) => s.section_id === selectedSection.section_id,
              );
              if (currentIndex === -1) return prev;
              const updated = [...prev.completedSections];
              updated[currentIndex] = updatedSection;
              return { completedSections: updated };
            });
          }

          // Trigger source reverification if backend is validating
          if (
            sourceVerificationEnabled &&
            result.data?.status === "validating"
          ) {
            onClearSectionCitations?.(selectedSection.section_id);
            onValidatingSection?.(selectedSection.section_id);
          }
        } else {
          // On error, restore the original section
          if (isCurrentSection) {
            updateGenerationState({ currentSection: originalSection });
          } else if (completedIndex !== -1) {
            updateGenerationState((prev) => {
              const currentIndex = prev.completedSections.findIndex(
                (s) => s.section_id === selectedSection.section_id,
              );
              if (currentIndex === -1) return prev;
              const restored = [...prev.completedSections];
              restored[currentIndex] = originalSection;
              return { completedSections: restored };
            });
          }
          registerError(
            `Failed to regenerate section: ${result.error || "Unknown error"}`,
          );
          setRegenerationError(result.error);
          setIsRegenerateModalOpen(true);
        }
      } catch (error) {
        console.error("Regeneration failed:", error);
        // On error, restore the original section
        if (isCurrentSection) {
          updateGenerationState({ currentSection: originalSection });
        } else if (completedIndex !== -1) {
          updateGenerationState((prev) => {
            const currentIndex = prev.completedSections.findIndex(
              (s) => s.section_id === selectedSection.section_id,
            );
            if (currentIndex === -1) return prev;
            const restored = [...prev.completedSections];
            restored[currentIndex] = originalSection;
            return { completedSections: restored };
          });
        }
        registerError("Failed to regenerate section");
        setRegenerationError("Failed to regenerate section");
        setIsRegenerateModalOpen(true);
      } finally {
        setRegeneratingSectionId(null);
      }
    },
    [
      selectedSection,
      regenerateBlockedReason,
      setRegenerationError,
      setIsRegenerateModalOpen,
      updateGenerationState,
      regenerateSection,
      documentId,
      workspace_id,
      registerError,
      setRegeneratingSectionId,
      // eslint-disable-next-line react-hooks/exhaustive-deps
    ],
  );

  // Memoized component for completed sections to prevent unnecessary re-renders
  interface CompletedSectionProps {
    section: GenerationSection;
    isComplete: boolean;
    regeneratingSectionId: number | null;
    editingSectionId: number | null;
    regenerateBlockedReason: string;
    sectionHtmlContent: string;
    onViewPrompt: (section: GenerationSection) => void;
    onRegenerateClick: (section: GenerationSection) => void;
    onEditClick: (section: GenerationSection) => void;
    renderSectionTitle: (section: GenerationSection) => JSX.Element;
    renderSectionContent: (section: GenerationSection) => JSX.Element | null;
    getTooltip: (
      sectionId: number,
      actionType: "view" | "regenerate" | "edit" | "qc",
      defaultMessage: string,
    ) => string;
    // QC props
    qcEnabled: boolean;
    qcStatus: "idle" | "running" | "completed" | "failed";
    qcSectionResult: QCSectionResult | undefined;
    onStartSectionQC: (sectionId: number, sectionIndex: number) => void;
    onAcceptFinding: (findingId: number, editedText?: string | null) => void;
    onDeclineFinding: (findingId: number) => void;
    onSubmitFeedback: (findingId: number, helpful: boolean) => void;
    onAcceptAllInSection: (sectionIndex: number) => void;
    onDeclineAllInSection: (sectionIndex: number) => void;
    onApplySectionChanges: (sectionIndex: number) => void;
    onOpenQCResults: (sectionIndex: number) => void;
  }

  const CompletedSection: React.FC<CompletedSectionProps> = React.memo(
    ({
      section,
      isComplete,
      regeneratingSectionId,
      editingSectionId,
      regenerateBlockedReason,
      sectionHtmlContent,
      onViewPrompt,
      onRegenerateClick,
      onEditClick,
      renderSectionTitle,
      renderSectionContent,
      getTooltip,
      qcEnabled,
      qcStatus,
      qcSectionResult,
      onStartSectionQC,
      onAcceptFinding,
      onDeclineFinding,
      onSubmitFeedback,
      onAcceptAllInSection,
      onDeclineAllInSection,
      onApplySectionChanges,
      onOpenQCResults,
    }) => {
      const sectionIndex = section.order_index ?? 0;
      const isSectionChecking = qcSectionResult?.status === "checking";
      const isSectionComplete =
        qcSectionResult?.status === "complete" &&
        (qcSectionResult?.findings.length ?? 0) > 0;
      const isSectionError = qcSectionResult?.status === "error";
      return (
        <div
          className={
            section.content && section.content.length > 0
              ? styles.section
              : styles.sectionNoContent
          }
          data-section-id={section.section_id}
          data-nav-section-id={section.section_id}
        >
          <div
            className={
              section.content && section.content.length > 0
                ? styles.sectionHeader
                : styles.sectionHeaderNoContent
            }
          >
            {renderSectionTitle(section)}
            {isComplete && section.content && section.content.length > 0 && (
              <div className={styles.sectionActions}>
                <Tooltip
                  placement="top"
                  title={getTooltip(
                    section.section_id,
                    "view",
                    "View the prompt used to generate this section",
                  )}
                >
                  <span style={{ display: "inline-block" }}>
                    <Button
                      size="sm"
                      variant="outlined"
                      color="neutral"
                      onClick={() => onViewPrompt(section)}
                      startDecorator={<VisibilityIcon />}
                      disabled={
                        regeneratingSectionId !== null ||
                        editingSectionId !== null ||
                        qcStatus === "running"
                      }
                      sx={{ backgroundColor: "background.surface" }}
                    >
                      View Prompt
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip
                  placement="top"
                  title={getTooltip(
                    section.section_id,
                    "regenerate",
                    "Regenerate this section",
                  )}
                >
                  <span style={{ display: "inline-block" }}>
                    <Button
                      size="sm"
                      variant="outlined"
                      color="neutral"
                      onClick={() => onRegenerateClick(section)}
                      startDecorator={<RefreshIcon />}
                      disabled={
                        regeneratingSectionId !== null ||
                        editingSectionId !== null ||
                        qcStatus === "running" ||
                        regenerateBlockedReason !== ""
                      }
                      sx={{ backgroundColor: "background.surface" }}
                    >
                      Regenerate
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip
                  placement="top"
                  title={getTooltip(
                    section.section_id,
                    "edit",
                    "Edit this section",
                  )}
                >
                  <span style={{ display: "inline-block" }}>
                    <Button
                      size="sm"
                      variant="outlined"
                      color="neutral"
                      onClick={() => onEditClick(section)}
                      disabled={
                        editingSectionId !== null ||
                        regeneratingSectionId !== null ||
                        qcStatus === "running"
                      }
                      startDecorator={<EditIcon />}
                      sx={{ backgroundColor: "background.surface" }}
                    >
                      Edit
                    </Button>
                  </span>
                </Tooltip>
                {qcEnabled && hasSourceDocuments && (
                  <Tooltip
                    placement="top"
                    title={getTooltip(
                      section.section_id,
                      "qc",
                      isSectionChecking
                        ? "Quality check in progress..."
                        : isSectionError
                          ? "Quality check failed for this section"
                          : isSectionComplete
                          ? "View quality check results"
                          : "Quality check this section against sources",
                    )}
                  >
                    <span style={{ display: "inline-block" }}>
                      <Button
                        size="sm"
                        variant={
                          isSectionComplete || isSectionError
                            ? "soft"
                            : "outlined"
                        }
                        color={
                          isSectionComplete || isSectionError
                            ? "warning"
                            : "neutral"
                        }
                        onClick={() =>
                          isSectionComplete || isSectionError
                            ? onOpenQCResults(sectionIndex)
                            : onStartSectionQC(section.section_id, sectionIndex)
                        }
                        startDecorator={
                          isSectionChecking ? (
                            <CircularProgress size="sm" />
                          ) : (
                            <FactCheckIcon />
                          )
                        }
                        disabled={
                          regeneratingSectionId !== null ||
                          editingSectionId !== null ||
                          isSectionChecking ||
                          (qcStatus === "running" && !isSectionComplete)
                        }
                        sx={{
                          backgroundColor:
                            isSectionComplete || isSectionError
                            ? undefined
                            : "background.surface",
                        }}
                      >
                        {isSectionChecking
                          ? "Checking..."
                          : isSectionError
                            ? qcSectionResult!.findings.length > 0
                              ? `${qcSectionResult!.findings.length} Finding${qcSectionResult!.findings.length !== 1 ? "s" : ""}`
                              : "QC Results"
                          : isSectionComplete
                            ? `${qcSectionResult!.findings.length} Finding${qcSectionResult!.findings.length !== 1 ? "s" : ""}`
                            : "QC"}
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </div>
            )}
          </div>
          <div style={{ position: "relative" }}>
            {(section.content ||
              regeneratingSectionId === section.section_id) &&
              renderSectionContent(section)}
            {isSectionChecking && (
              <div className={styles.localOverlay}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size="sm" />
                  <Typography level="body-sm">
                    Checking section against sources...
                  </Typography>
                </Stack>
              </div>
            )}
          </div>
        </div>
      );
    },
    (prevProps, nextProps) => {
      // Only rerender if these change
      return (
        prevProps.section.section_id === nextProps.section.section_id &&
        prevProps.section.content === nextProps.section.content &&
        prevProps.section.title === nextProps.section.title &&
        prevProps.isComplete === nextProps.isComplete &&
        prevProps.regeneratingSectionId === nextProps.regeneratingSectionId &&
        prevProps.editingSectionId === nextProps.editingSectionId &&
        prevProps.sectionHtmlContent === nextProps.sectionHtmlContent &&
        prevProps.qcEnabled === nextProps.qcEnabled &&
        prevProps.qcStatus === nextProps.qcStatus &&
        prevProps.qcSectionResult === nextProps.qcSectionResult
      );
    },
  );

  CompletedSection.displayName = "CompletedSection";

  // Component for rendering individual thinking events
  const ThinkingEventItem: React.FC<{ event: ThinkingEvent }> = ({ event }) => {
    switch (event.type) {
      case "status":
        return (
          <Typography
            level="body-xs"
            sx={{ color: "neutral.500", fontStyle: "italic" }}
          >
            {event.content}
          </Typography>
        );
      case "tool_call": {
        const args = event.args as Record<string, unknown> | undefined;
        let detail = "";
        if (event.tool === "search_sources" && args?.query) {
          detail = `: "${args.query}"`;
        } else if (event.tool === "get_source_chunk" && args?.source_id) {
          detail = ` from source #${args.source_id}`;
        }
        return (
          <Typography
            level="body-xs"
            sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
          >
            <SearchIcon sx={{ fontSize: 12 }} /> {event.description || event.tool}
            {detail}
          </Typography>
        );
      }
      case "tool_result": {
        // Result structure: { success: boolean, result: { results?: [], total?: number, content?: string, source_name?: string, ... } }
        let resultText = "completed";
        let sourceInfo = "";
        try {
          const toolResult = event.result as
            | Record<string, unknown>
            | undefined;
          const innerResult = toolResult?.result as
            | Record<string, unknown>
            | undefined;

          if (innerResult) {
            const results = innerResult.results as
              | Array<{ source_name?: string; relevance?: number }>
              | undefined;
            const total = innerResult.total as number | undefined;
            const content = innerResult.content as string | undefined;
            const sourceName = innerResult.source_name as string | undefined;
            const startPos = innerResult.start_position as number | undefined;
            const endPos = innerResult.end_position as number | undefined;

            if (results && results.length > 0) {
              resultText = `${results.length} results`;
              // Show top source names
              const sourceNames = [
                ...new Set(
                  results
                    .slice(0, 3)
                    .map((r) => r.source_name)
                    .filter(Boolean),
                ),
              ];
              if (sourceNames.length > 0) {
                sourceInfo = ` in ${sourceNames.join(", ")}${results.length > 3 ? "..." : ""}`;
              }
            } else if (total && total > 0) {
              resultText = `${total} results`;
            } else if (content && sourceName) {
              // Chunk retrieval - show source name and position
              const posInfo =
                startPos !== undefined ? ` (chars ${startPos}-${endPos})` : "";
              resultText = `chunk from "${sourceName}"${posInfo}`;
            } else if (content) {
              resultText = "1 chunk retrieved";
            } else {
              resultText = "0 results";
            }
          }
        } catch (e) {
          console.error(
            "[ThinkingEventItem] Error parsing tool_result:",
            e,
            event,
          );
        }

        return (
          <Typography
            level="body-xs"
            sx={{ color: "success.600", display: "flex", alignItems: "center", gap: 0.5 }}
          >
            <CheckIcon sx={{ fontSize: 12 }} /> Found {resultText}
            {sourceInfo}
          </Typography>
        );
      }
      case "reasoning":
        return (
          <Typography level="body-sm" sx={{ mt: 1, whiteSpace: "pre-wrap" }}>
            {event.content}
          </Typography>
        );
      case "error":
        return (
          <Typography
            level="body-xs"
            sx={{ color: "danger.600", display: "flex", alignItems: "center", gap: 0.5 }}
          >
            <WarningAmberIcon sx={{ fontSize: 12 }} /> {event.content}
          </Typography>
        );
      default:
        return null;
    }
  };

  // Component for rendering thinking sections with collapsible UI
  interface ThinkingSectionProps {
    section: GenerationSection;
    isExpanded: boolean;
    onToggleExpand: () => void;
    renderSectionTitle: (section: GenerationSection) => JSX.Element;
  }

  const ThinkingSection: React.FC<ThinkingSectionProps> = ({
    section,
    isExpanded,
    onToggleExpand,
    renderSectionTitle,
  }) => {
    const events = section.thinking_events || [];
    const currentEvent = events[events.length - 1];

    // Determine current status text
    const getStatusText = () => {
      if (section.status === "complete") return "Research complete";
      if (!currentEvent) return "Thinking...";
      if (currentEvent.type === "tool_call")
        return currentEvent.description || "Searching...";
      if (currentEvent.type === "status")
        return currentEvent.content || "Thinking...";
      return "Processing...";
    };

    return (
      <div
        className={styles.thinkingSection}
        // Navigation anchor only — deliberately not data-section-id, which the
        // source-verification logic treats as a citation container.
        data-nav-section-id={section.section_id}
      >
        <div className={styles.thinkingSectionHeader}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
            <Chip
              size="sm"
              variant="soft"
              color="primary"
              startDecorator={<PsychologyIcon sx={{ fontSize: 14 }} />}
            >
              Thinking
            </Chip>
            {renderSectionTitle(section)}
            <Typography level="body-xs" sx={{ color: "neutral.500", ml: 1 }}>
              — {getStatusText()}
            </Typography>
          </Box>
          <IconButton
            size="sm"
            variant="plain"
            color="neutral"
            onClick={onToggleExpand}
            sx={{ ml: 1 }}
          >
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </div>

        {isExpanded && (
          <>
            {events.length > 0 && (
              <div className={styles.thinkingLog}>
                {events.map((event, i) => (
                  <ThinkingEventItem key={i} event={event} />
                ))}
              </div>
            )}

            {section.status === "complete" && section.content && (
              <div className={styles.thinkingOutput}>
                <Typography level="body-sm" fontWeight="md" sx={{ mb: 1 }}>
                  Research Summary:
                </Typography>
                <div className={styles.sectionContent}>
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[[rehypeSanitize, rehypeSanitizeSchema]]}
                    className="markdown-body"
                  >
                    {section.content}
                  </ReactMarkdown>
                </div>
              </div>
            )}
          </>
        )}

        {section.status === "generating" && (
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1, px: 1 }}
          >
            <CircularProgress size="sm" />
            <Typography level="body-xs" color="neutral">
              Researching sources...
            </Typography>
          </Box>
        )}
      </div>
    );
  };

  // Create editor per section that's being edited
  const [editingSectionId, setEditingSectionId] = useState<number | null>(null);
  const [sectionHtmlContent, setSectionHtmlContent] = useState<string>("");

  const handleEditClick = useCallback((section: GenerationSection) => {
    // Get the current rendered HTML for this specific section
    const sectionElement = document.querySelector(
      `.section-${section.section_id} .sectionContent .markdown-body`,
    );

    if (sectionElement) {
      const content = stripDataRefSpans(sectionElement.innerHTML);
      setSectionHtmlContent(content);
      setEditingSectionId(section.section_id);
    } else {
      // Fallback selector if the first one fails
      const altElement = document.querySelector(
        `.section-${section.section_id} .sectionContent`,
      );
      if (altElement) {
        const content = stripDataRefSpans(altElement.innerHTML);
        setSectionHtmlContent(content);
        setEditingSectionId(section.section_id);
      } else {
        console.error("Could not find section element with any selector");
      }
    }
  }, []);

  const handleSaveEdit = useCallback(
    async (section: GenerationSection, content: string) => {
      try {
        // Update the backend first with HTML content
        const result = await updateSection(
          documentId,
          section.section_id,
          content,
          workspace_id,
        );

        if (!result.success) {
          console.error("Failed to save section:", result.error);
          registerError(
            `Failed to save section: ${result.error || "Unknown error"}`,
          );
          return;
        }

        // Use server-returned content (data-ref spans stripped) to avoid phantom underlines
        const cleanContent = result.data?.content ?? content;

        // If backend update successful, update the UI state with clean content
        updateGenerationState((prev) => {
          if (prev.currentSection?.section_id === section.section_id) {
            return {
              currentSection: { ...prev.currentSection, content: cleanContent },
            };
          }
          const idx = prev.completedSections.findIndex(
            (s) => s.section_id === section.section_id,
          );
          if (idx === -1) return {};
          const updated = [...prev.completedSections];
          updated[idx] = { ...updated[idx], content: cleanContent };
          return { completedSections: updated };
        });
        // Trigger citation reverification if source verification is enabled
        if (sourceVerificationEnabled && result.data?.status === "validating") {
          onClearSectionCitations?.(section.section_id);
          onValidatingSection?.(section.section_id);
        }

        setEditingSectionId(null);
      } catch (error) {
        console.error("Error saving section:", error);
        registerError("Failed to save section changes");
      }
    },
    [
      documentId,
      workspace_id,
      updateSection,
      registerError,
      updateGenerationState,
      sourceVerificationEnabled,
      onClearSectionCitations,
      onValidatingSection,
    ],
  );

  const renderSectionTitle = useCallback((section: GenerationSection) => {
    const level = section.level || 1;
    if (level >= 5) {
      return (
        <div className={styles.sectionTitle}>
          <strong>{section.title}</strong>
        </div>
      );
    }
    // Use appropriate heading level (h1-h4) for levels 1-4
    const tag = `h${level}`;
    return (
      <div className={styles.sectionTitle}>
        {React.createElement(tag, null, section.title)}
      </div>
    );
  }, []);

  const renderSectionContent = useCallback(
    (section: GenerationSection) => {
      // Skip rendering for completion messages
      if (section.status === "complete" && !section.section_id) {
        return null;
      }

      // Validate actual sections
      if (!section || !section.section_id) {
        return null;
      }

      // Calculate base heading level for this section
      const baseLevel = section.level || 1;

      // Custom components configuration for ReactMarkdown
      const components: Components = {
        h1: ({ node, children, ...props }) => {
          const adjustedLevel = Math.min(baseLevel, 4);
          return React.createElement(`h${adjustedLevel}`, props, children);
        },
        h2: ({ node, children, ...props }) => {
          const adjustedLevel = Math.min(baseLevel + 1, 4);
          return adjustedLevel > 4 ? (
            <strong {...props}>{children}</strong>
          ) : (
            React.createElement(`h${adjustedLevel}`, props, children)
          );
        },
        h3: ({ node, children, ...props }) => {
          const adjustedLevel = Math.min(baseLevel + 2, 4);
          return adjustedLevel > 4 ? (
            <strong {...props}>{children}</strong>
          ) : (
            React.createElement(`h${adjustedLevel}`, props, children)
          );
        },
        h4: ({ node, children, ...props }) => {
          const adjustedLevel = Math.min(baseLevel + 3, 4);
          return adjustedLevel > 4 ? (
            <strong {...props}>{children}</strong>
          ) : (
            React.createElement(`h${adjustedLevel}`, props, children)
          );
        },
        h5: ({ node, children, ...props }) => {
          return <strong {...props}>{children}</strong>;
        },
        h6: ({ node, children, ...props }) => {
          return <strong {...props}>{children}</strong>;
        },
        td: ({ node, children, ...props }) => {
          // Check if the content contains bullet points
          const content = String(children);
          if (content.includes("•")) {
            const items = content.split("•").filter((item) => item.trim());
            return (
              <td {...props}>
                <ul className={styles.tableCellList}>
                  {items.map((item, index) => (
                    <li key={index}>{item.trim()}</li>
                  ))}
                </ul>
              </td>
            );
          }
          return <td {...props}>{children}</td>;
        },
      };

      // For current section being typed out
      if (section === generationState.currentSection) {
        return (
          <div
            className={`${styles.sectionContent} ${regeneratingSectionId === section.section_id ? styles.regenerating : ""} section-${section.section_id}`}
          >
            {regeneratingSectionId === section.section_id && (
              <div className={styles.localOverlay}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <CircularProgress size="lg" />
                  <Typography level="body-sm">Regenerating...</Typography>
                </Box>
              </div>
            )}
            <div className="sectionContent">
              <div className="markdown-body">
                {isHtmlContent(section.content) ? (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(section.content),
                    }}
                  />
                ) : (
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[[rehypeSanitize, rehypeSanitizeSchema]]}
                    components={components}
                  >
                    {section.content}
                  </ReactMarkdown>
                )}
              </div>
            </div>
          </div>
        );
      }

      // For completed sections
      return (
        <div
          className={`${styles.sectionContent} ${regeneratingSectionId === section.section_id ? styles.regenerating : ""} section-${section.section_id}`}
        >
          {regeneratingSectionId === section.section_id && (
            <div className={styles.localOverlay}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <CircularProgress size="lg" />
                <Typography level="body-sm">Regenerating...</Typography>
              </Box>
            </div>
          )}
          <div className="sectionContent">
            {editingSectionId === section.section_id ? (
              <RichTextEditor
                content={sectionHtmlContent}
                onSave={(content) => handleSaveEdit(section, content)}
                onCancel={() => setEditingSectionId(null)}
              />
            ) : (
              <div className="markdown-body">
                {isHtmlContent(section.content) ? (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(section.content),
                    }}
                  />
                ) : (
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[[rehypeSanitize, rehypeSanitizeSchema]]}
                    components={components}
                  >
                    {section.content}
                  </ReactMarkdown>
                )}
              </div>
            )}
          </div>
        </div>
      );
    },
    [
      generationState.currentSection,
      regeneratingSectionId,
      editingSectionId,
      sectionHtmlContent,
      handleSaveEdit,
    ],
  );

  // Keep this cleanup effect but clean up the logging
  useEffect(() => {
    if (generationState.isComplete) {
      updateGenerationState((prev) => {
        const stuckSections = [
          ...prev.completedSections,
          prev.currentSection,
        ].filter(
          (section) => section?.status === "generating" && section?.content,
        );
        if (stuckSections.length === 0) return {};
        console.warn(
          `Found ${stuckSections.length} sections stuck in generating state`,
        );
        return {
          completedSections: prev.completedSections.map((section) =>
            section.status === "generating" && section.content
              ? { ...section, status: "complete" as const }
              : section,
          ),
          currentSection:
            prev.currentSection?.status === "generating"
              ? { ...prev.currentSection, status: "complete" as const }
              : prev.currentSection,
        };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generationState.isComplete]);

  const [isPromptPreviewOpen, setIsPromptPreviewOpen] = useState(false);
  const [promptPreviewData, setPromptPreviewData] = useState<{
    prompt?: string;
    isLoading: boolean;
  }>({
    isLoading: false,
  });

  const handleViewPrompt = useCallback(
    async (section: GenerationSection) => {
      setPromptPreviewData({ isLoading: true });
      setIsPromptPreviewOpen(true);

      const result = await getSectionPrompt(
        documentId,
        section.section_id,
        workspace_id,
      );
      if (result.success && result.data) {
        setPromptPreviewData({
          prompt: result.data.prompt,
          isLoading: false,
        });
      } else {
        setPromptPreviewData({
          prompt: `Error loading prompt: ${result.error || "Unknown error"}`,
          isLoading: false,
        });
      }
    },
    [documentId, workspace_id, getSectionPrompt],
  );

  // Helper function to get appropriate tooltip message based on section state
  const getTooltip = useCallback(
    (
      sectionId: number,
      actionType: "view" | "regenerate" | "edit" | "qc",
      defaultMessage: string,
    ): string => {
      const isCurrentSectionRegenerating = regeneratingSectionId === sectionId;
      const isAnotherSectionRegenerating =
        regeneratingSectionId !== null && regeneratingSectionId !== sectionId;
      const isCurrentSectionEditing = editingSectionId === sectionId;
      const isAnotherSectionEditing =
        editingSectionId !== null && editingSectionId !== sectionId;

      // Priority: current section regenerating > current section editing > another section regenerating > another section editing > QC running
      if (isCurrentSectionRegenerating) {
        return "This section is being regenerated";
      }
      if (isCurrentSectionEditing) {
        return "This section is being edited";
      }
      if (isAnotherSectionRegenerating) {
        return "Another section is being regenerated";
      }
      if (isAnotherSectionEditing) {
        return "Another section is being edited";
      }
      if (actionType === "qc" && qcStatus === "running") {
        return "Quality check is already running";
      }
      if (actionType === "regenerate" && regenerateBlockedReason) {
        return regenerateBlockedReason;
      }
      return defaultMessage;
    },
    [regeneratingSectionId, editingSectionId, qcStatus, regenerateBlockedReason],
  );

  // Handle generation errors via global error system (with deduplication)
  const lastReportedError = useRef<string | null>(null);
  React.useEffect(() => {
    if (
      generationState.error &&
      generationState.error !== lastReportedError.current
    ) {
      registerError(`Generation error: ${generationState.error}`);
      lastReportedError.current = generationState.error;
    } else if (!generationState.error) {
      lastReportedError.current = null; // Reset when error clears
    }
  }, [generationState.error, registerError]);

  // Completed, currently-generating and queued sections merged into document
  // order. Shared by the rendered document and the floating outline so both
  // always agree on what the document contains.
  const orderedSections = useMemo(
    () =>
      [
        ...generationState.completedSections,
        ...(generationState.currentSection
          ? [generationState.currentSection]
          : []),
        ...generationState.pendingSections,
      ].sort((a, b) => (a.order_index ?? 999) - (b.order_index ?? 999)),
    [
      generationState.completedSections,
      generationState.currentSection,
      generationState.pendingSections,
    ],
  );

  const navNodes = useMemo(
    () => buildTreeFromGenerationSections(orderedSections),
    [orderedSections],
  );

  return (
    <div className={styles.container} style={{ position: "relative" }}>
      {/* Verify sources button is rendered by the parent (GenerateDocument) */}
      <div className={styles.content}>
        {/* Initial loading state */}
        {!generationState.currentSection &&
          !generationState.completedSections.length && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <Loading />
            </Box>
          )}

        {/* All sections sorted by order_index — completed, currently-generating, and queued merged */}
        {orderedSections.map((section, index) => {
          if (section.section_type === "Thinking") {
            return (
              <ThinkingSection
                key={`thinking-${section.section_id}`}
                section={section}
                isExpanded={expandedThinkingSections.has(index)}
                onToggleExpand={() => toggleThinkingExpanded(index)}
                renderSectionTitle={renderSectionTitle}
              />
            );
          }
          if (section === generationState.currentSection) {
            return (
              <div
                key={`current-${section.section_id}`}
                className={styles.section}
              >
                <div className={styles.sectionHeader}>
                  {renderSectionTitle(section)}
                  {generationState.isComplete &&
                    section.content &&
                    section.content.length > 0 && (
                      <div className={styles.sectionActions}>
                        <Tooltip
                          placement="top"
                          title={getTooltip(
                            section.section_id,
                            "view",
                            "View the prompt used to generate this section",
                          )}
                        >
                          <span style={{ display: "inline-block" }}>
                            <Button
                              size="sm"
                              variant="outlined"
                              color="neutral"
                              disabled={
                                regeneratingSectionId !== null ||
                                editingSectionId !== null
                              }
                              onClick={() => handleViewPrompt(section)}
                              startDecorator={<VisibilityIcon />}
                              sx={{ backgroundColor: "background.surface" }}
                            >
                              View Prompt
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip
                          placement="top"
                          title={getTooltip(
                            section.section_id,
                            "regenerate",
                            "Regenerate this section",
                          )}
                        >
                          <span style={{ display: "inline-block" }}>
                            <Button
                              size="sm"
                              variant="outlined"
                              color="neutral"
                              disabled={
                                regeneratingSectionId !== null ||
                                editingSectionId !== null ||
                                regenerateBlockedReason !== ""
                              }
                              onClick={() => handleRegenerateClick(section)}
                              startDecorator={<RefreshIcon />}
                              sx={{ backgroundColor: "background.surface" }}
                            >
                              Regenerate
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip
                          placement="top"
                          title={getTooltip(
                            section.section_id,
                            "edit",
                            "Edit this section",
                          )}
                        >
                          <span style={{ display: "inline-block" }}>
                            <Button
                              size="sm"
                              variant="outlined"
                              color="neutral"
                              onClick={() => handleEditClick(section)}
                              disabled={
                                editingSectionId !== null ||
                                regeneratingSectionId !== null
                              }
                              startDecorator={<EditIcon />}
                              sx={{ backgroundColor: "background.surface" }}
                            >
                              Edit
                            </Button>
                          </span>
                        </Tooltip>
                      </div>
                    )}
                </div>
                {renderSectionContent(section)}
              </div>
            );
          }
          return (
            <div
              key={`section-${section.section_id}`}
              style={isPanelOpen ? { display: "flex", gap: 0 } : undefined}
            >
              <div style={isPanelOpen ? { flex: 1, minWidth: 0 } : undefined}>
                <CompletedSection
                  section={section}
                  isComplete={generationState.isComplete}
                  regeneratingSectionId={regeneratingSectionId}
                  editingSectionId={editingSectionId}
                  regenerateBlockedReason={regenerateBlockedReason}
                  sectionHtmlContent={sectionHtmlContent}
                  onViewPrompt={handleViewPrompt}
                  onRegenerateClick={handleRegenerateClick}
                  onEditClick={handleEditClick}
                  renderSectionTitle={renderSectionTitle}
                  renderSectionContent={renderSectionContent}
                  getTooltip={getTooltip}
                  qcEnabled={qcEnabled}
                  qcStatus={qcStatus}
                  qcSectionResult={qcSectionResults.get(
                    section.order_index ?? 0,
                  )}
                  onStartSectionQC={handleStartSectionQC}
                  onAcceptFinding={handleAcceptFinding}
                  onDeclineFinding={handleDeclineFinding}
                  onSubmitFeedback={handleSubmitFeedback}
                  onAcceptAllInSection={handleAcceptAllInSection}
                  onDeclineAllInSection={handleDeclineAllInSection}
                  onApplySectionChanges={handleApplySectionChanges}
                  onOpenQCResults={(idx) => setQcModalSectionIndex(idx)}
                />
            </div>
            {isPanelOpen && (
              <div
                style={{
                  width: 300,
                  flexShrink: 0,
                  borderLeft: "1px solid #f4f4f5",
                  padding: "8px",
                  alignSelf: "stretch",
                }}
              >
                {validationStatus[section.section_id] === "validating" ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      py: 1,
                    }}
                  >
                    <CircularProgress size="sm" />
                    <Typography level="body-xs" color="neutral">
                      Verifying...
                    </Typography>
                  </Box>
                ) : !citationsLoaded ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      py: 1,
                    }}
                  >
                    <CircularProgress size="sm" />
                    <Typography level="body-xs" color="neutral">
                      Loading citations...
                    </Typography>
                  </Box>
                ) : (citations[section.section_id] || []).length === 0 &&
                  !validationErrors[section.section_id] ? (
                  <Typography
                    level="body-xs"
                    sx={{
                      color: "neutral.400",
                      fontStyle: "italic",
                      py: 0.5,
                    }}
                  >
                    No citations
                  </Typography>
                ) : (citations[section.section_id] || []).length === 0 &&
                  validationErrors[section.section_id] ? (
                  <Alert
                    size="sm"
                    color="warning"
                    variant="soft"
                    sx={{ my: 0.5 }}
                  >
                    <Typography level="body-xs">
                      {validationErrors[section.section_id]}
                    </Typography>
                  </Alert>
                ) : (
                  <>
                    {validationErrors[section.section_id] && (
                      <Alert
                        size="sm"
                        color="warning"
                        variant="soft"
                        sx={{ mb: 0.5 }}
                      >
                        <Typography level="body-xs">
                          {validationErrors[section.section_id]}
                        </Typography>
                      </Alert>
                    )}
                    <div className={panelStyles.referenceList}>
                      {[...(citations[section.section_id] || [])]
                        .sort((a, b) => a.order_index - b.order_index)
                        .map((citation) => (
                          <ReferenceItem
                            key={citation.ref_id}
                            citation={citation}
                            isExpanded={
                              expandedRef?.sectionId === section.section_id &&
                              expandedRef?.refId === citation.ref_id
                            }
                            onClick={() =>
                              onExpandRef?.(
                                section.section_id,
                                citation.ref_id,
                              )
                            }
                            onViewPage={(sourceDocumentId, page) =>
                              setPdfView({
                                sourceDocumentId,
                                page,
                                sourceFilename: citation.source_filename,
                              })
                            }
                            onClickInEditor={() => {
                              const sectionEl = document.querySelector(
                                `[data-section-id="${section.section_id}"]`,
                              );
                              if (!sectionEl) return;
                              const refSpan = sectionEl.querySelector(
                                `[data-ref="${citation.ref_id}"]`,
                              ) as HTMLElement | null;
                              if (refSpan) {
                                refSpan.scrollIntoView({
                                  behavior: "smooth",
                                  block: "center",
                                });
                                refSpan.classList.add(
                                  "citation-highlight-pulse",
                                );
                                setTimeout(
                                  () =>
                                    refSpan.classList.remove(
                                      "citation-highlight-pulse",
                                    ),
                                  600,
                                );
                              }
                            }}
                          />
                        ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
          );
        })}

        {/* Generating more content indicator — shown below all sections while waiting for next */}
        {generationState.isStreamComplete &&
          !generationState.isComplete && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
              <CircularProgress size="sm" />
              <Typography level="body-sm" color="neutral">
                Generating more content...
              </Typography>
            </Box>
          )}

        {/* Waiting for next section (no current, no pending, but still generating) */}
        {generationState.isGenerating &&
          !generationState.currentSection &&
          generationState.pendingSections.length === 0 &&
          !generationState.isComplete && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
              <CircularProgress size="sm" />
              <Typography level="body-sm" color="neutral">
                Preparing next section...
              </Typography>
            </Box>
          )}

        {/* Server-side generation is in progress but the client isn't streaming.
            The automatic reconnect can give up (or be blocked after the first
            attempt), so offer an explicit Reconnect action instead of a
            spinner that may never resolve. */}
        {!generationState.isComplete &&
          !generationState.isGenerating &&
          documentStatus === "Started" &&
          generationState.completedSections.length > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
              <Typography level="body-sm" color="neutral">
                Disconnected from generation. It may still be running on the
                server.
              </Typography>
              <Button
                size="sm"
                variant="outlined"
                color="neutral"
                startDecorator={<RefreshIcon />}
                onClick={() => startGeneration(true)}
              >
                Reconnect
              </Button>
            </Box>
          )}

        {generationState.isComplete && (
          <div className={styles.actionContainer} style={{ gap: "8px" }}>
            {/* Verify Sources button moved to sticky top bar */}
            {qcEnabled && hasSourceDocuments && qcStatus === "idle" && (
              <Button
                variant="soft"
                color="neutral"
                onClick={handleStartAllSectionsQC}
                startDecorator={<FactCheckIcon />}
                sx={{
                  minWidth: 140,
                  whiteSpace: "nowrap",
                  mr: 1,
                }}
              >
                Quality Check
              </Button>
            )}
            {qcStatus === "running" && qcActiveSectionIndex === null && (
              <Button
                variant="soft"
                color="neutral"
                disabled
                startDecorator={<CircularProgress size="sm" />}
                sx={{
                  minWidth: 140,
                  whiteSpace: "nowrap",
                  mr: 1,
                }}
              >
                Checking...
              </Button>
            )}
            {qcStatus === "completed" &&
              (qcTotalFindings > 0 ||
                Array.from(qcSectionResults.values()).some(
                  (section) => section.status === "error",
                )) && (
              <Button
                variant="soft"
                color="warning"
                onClick={() => {
                  // Open modal for first section with findings, otherwise first section with an error.
                  for (const [idx, sec] of qcSectionResults) {
                    if (sec.findings.length > 0) {
                      setQcModalSectionIndex(idx);
                      return;
                    }
                  }
                  for (const [idx, sec] of qcSectionResults) {
                    if (sec.status === "error") {
                      setQcModalSectionIndex(idx);
                      return;
                    }
                  }
                  const firstSection = qcSectionResults.keys().next();
                  if (!firstSection.done) {
                    setQcModalSectionIndex(firstSection.value);
                  }
                }}
                startDecorator={<FactCheckIcon />}
                sx={{
                  minWidth: 140,
                  whiteSpace: "nowrap",
                  mr: 1,
                }}
              >
                {qcTotalFindings > 0
                  ? `${qcTotalFindings} Finding${qcTotalFindings !== 1 ? "s" : ""}`
                  : "QC Results"}
              </Button>
            )}
            {(qcStatus === "completed" || qcStatus === "failed") && (
              <Button
                variant="outlined"
                color="neutral"
                onClick={handleDismissQC}
                sx={{
                  minWidth: 100,
                  whiteSpace: "nowrap",
                  mr: 1,
                }}
              >
                Dismiss QC
              </Button>
            )}
            <Button
              variant="solid"
              color={
                generationState.exportState === "failed" ? "danger" : "primary"
              }
              onClick={
                generationState.exportState === "ready"
                  ? handleDownload
                  : handleExport
              }
              disabled={
                generationState.exportState === "exporting" || downloadingExport
              }
              startDecorator={
                generationState.exportState === "exporting" ||
                downloadingExport ? (
                  <CircularProgress
                    size="sm"
                    determinate={false}
                    sx={{
                      "--CircularProgress-progressColor": "#fff !important",
                      "--CircularProgress-trackColor": "rgba(255,255,255,0.3)",
                      color: "#fff !important",
                    }}
                  />
                ) : undefined
              }
              sx={{ minWidth: 120, whiteSpace: "nowrap" }}
            >
              {generationState.exportState === "exporting"
                ? "Exporting"
                : downloadingExport
                  ? "Downloading"
                  : getExportButtonText()}
            </Button>
          </div>
        )}
      </div>

      {/* Outline sits above the document rather than beside it. This view
          already splits the page between the config/source panel and the
          preview, and Page caps the layout at 1200px, so the preview column
          is roughly 620px at any screen size — a side rail would take nearly
          half of what is left. */}
      <StructureNavigator
        nodes={navNodes}
        storageKey="meridian_generation_nav_hidden"
        sideColumnFrom="never"
      />

      {/* QC Results Modal */}
      {qcModalSectionIndex !== null &&
        qcSectionResults.get(qcModalSectionIndex) && (
          <Modal open={true} onClose={() => setQcModalSectionIndex(null)}>
            <ModalDialog
              sx={{
                maxWidth: 800,
                width: "90vw",
                maxHeight: "85vh",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <ModalClose />
              <DialogTitle>
                <Stack direction="row" spacing={1} alignItems="center">
                  <FactCheckIcon />
                  <Typography>
                    Quality Check
                    {qcTotalFindings > 0
                      ? `: ${qcSectionResults.get(qcModalSectionIndex)!.sectionTitle}`
                      : " Results"}
                  </Typography>
                </Stack>
              </DialogTitle>
              <Divider />
              <DialogContent sx={{ overflow: "auto" }}>
                {(() => {
                  const modalSection =
                    qcSectionResults.get(qcModalSectionIndex)!;
                  const sectionsWithFindings = Array.from(
                    qcSectionResults.values(),
                  ).filter((s) => s.findings.length > 0);
                  const sectionsWithErrors = Array.from(
                    qcSectionResults.values(),
                  ).filter((s) => s.status === "error");
                  const sectionsForNavigation = Array.from(
                    qcSectionResults.values(),
                  ).filter((s) => s.findings.length > 0 || s.status === "error");

                  // No findings at all
                  if (qcTotalFindings === 0 && sectionsWithErrors.length === 0) {
                    return (
                      <Box sx={{ textAlign: "center", py: 4 }}>
                        <Typography level="title-md" sx={{ mb: 1 }}>
                          No issues found
                        </Typography>
                        <Typography level="body-sm" color="neutral">
                          The quality check completed without finding any
                          issues.
                        </Typography>
                      </Box>
                    );
                  }
                  return (
                    <Stack spacing={2}>
                      {sectionsWithErrors.length > 0 && (
                        <Alert color="warning" variant="soft">
                          <QCSectionErrorNotice sections={sectionsWithErrors} />
                        </Alert>
                      )}
                      {/* Navigation for multi-section QC */}
                      {sectionsForNavigation.length > 1 && (
                        <Box>
                          <Typography
                            level="body-sm"
                            color="neutral"
                            sx={{ mb: 0.75 }}
                          >
                            Jump to section
                          </Typography>
                          <Select
                            value={qcModalSectionIndex}
                            onChange={(_, value) => {
                              if (value !== null) {
                                setQcModalSectionIndex(value);
                              }
                            }}
                            size="sm"
                          >
                            {sectionsForNavigation.map((s) => (
                              <Option
                                key={s.sectionIndex}
                                value={s.sectionIndex}
                              >
                                {s.sectionTitle}{" "}
                                {s.status === "error"
                                  ? `(${s.findings.length}, QC issue)`
                                  : `(${s.findings.length})`}
                              </Option>
                            ))}
                          </Select>
                        </Box>
                      )}
                      {modalSection.findings.length > 0 ? (
                        <QCSectionPanel
                          sectionTitle={modalSection.sectionTitle}
                          sectionContent={modalSection.sectionContent}
                          findings={modalSection.findings}
                          onAcceptFinding={handleAcceptFinding}
                          onDeclineFinding={handleDeclineFinding}
                          onRevertFinding={handleRevertFinding}
                          onSubmitFeedback={handleSubmitFeedback}
                          onAcceptAll={() =>
                            handleAcceptAllInSection(qcModalSectionIndex)
                          }
                          onDeclineAll={() =>
                            handleDeclineAllInSection(qcModalSectionIndex)
                          }
                          onRevertAll={() =>
                            handleRevertAllInSection(qcModalSectionIndex)
                          }
                        />
                      ) : (
                        <Box sx={{ textAlign: "center", py: 4 }}>
                          <Typography level="title-md" sx={{ mb: 1 }}>
                            No findings available for this section
                          </Typography>
                          <Typography level="body-sm" color="neutral">
                            QC could not process one or more suggestions here,
                            but no usable findings were returned for this
                            section.
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  );
                })()}
              </DialogContent>
              <Divider />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 1,
                  pt: 1,
                }}
              >
                <Button
                  variant="plain"
                  color="neutral"
                  disabled={qcApplyingSectionIndex !== null}
                  onClick={() => {
                    setQcModalSectionIndex(null);
                    if (qcTotalFindings === 0) handleDismissQC();
                  }}
                >
                  {qcTotalFindings === 0 ? "Done" : "Close"}
                </Button>
                {qcTotalFindings > 0 && (
                  <Button
                    variant="solid"
                    color="primary"
                    loading={qcApplyingSectionIndex === qcModalSectionIndex}
                    disabled={
                      !qcSectionResults.get(qcModalSectionIndex)?.findings
                        .length || qcApplyingSectionIndex !== null
                    }
                    onClick={() =>
                      handleApplySectionChanges(qcModalSectionIndex)
                    }
                  >
                    Apply Accepted Changes
                  </Button>
                )}
              </Box>
            </ModalDialog>
          </Modal>
        )}
      {/* <Modal
        isOpen={isRegenerateModalOpen}
        title="Regenerate Section"
        onClose={handleCloseRegenerateModal}
        onSubmit={handleRegenerateSection}
        fields={regenerateModalFields}
        buttons={regenerateModalButtons}
      /> */}
      <Modal
        open={isRegenerateModalOpen}
        onClose={() => {
          setIsRegenerateModalOpen(false);
          setRegenerationError(null);
        }}
      >
        <ModalDialog sx={{ width: 600, maxWidth: "90vw" }}>
          <DialogTitle>Regenerate Section</DialogTitle>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              handleRegenerateSection({
                prompt: formData.get("prompt") as string,
              });
            }}
          >
            <Stack spacing={2}>
              <Typography level="body-sm">
                Enter a custom prompt to regenerate this section:
              </Typography>
              <Textarea
                name="prompt"
                defaultValue={selectedSection?.prompt}
                required
                minRows={4}
                maxRows={16}
                placeholder="Enter section prompt..."
              />
              {regenerationError && (
                <Typography level="body-sm" color="danger">
                  {regenerationError}
                </Typography>
              )}
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  color="neutral"
                  onClick={() => {
                    setIsRegenerateModalOpen(false);
                    setRegenerationError(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="solid"
                  color="primary"
                  disabled={!!regenerationError?.includes("Rate limit")}
                >
                  {regenerationError?.includes("Rate limit")
                    ? "Rate Limited"
                    : "Regenerate"}
                </Button>
              </Stack>
            </Stack>
          </form>
        </ModalDialog>
      </Modal>
      <PromptPreviewModal
        isOpen={isPromptPreviewOpen}
        onClose={() => {
          setIsPromptPreviewOpen(false);
          setPromptPreviewData({ isLoading: false });
        }}
        prompt={promptPreviewData.prompt}
        isLoading={promptPreviewData.isLoading}
      />
      {pdfView && (
        <PdfViewer
          documentId={documentId}
          sourceDocumentId={pdfView.sourceDocumentId}
          initialPage={pdfView.page}
          sourceFilename={pdfView.sourceFilename}
          onClose={() => setPdfView(null)}
        />
      )}
    </div>
  );
};

export default GenerationView;
